import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Container from './Container';

/**
 * Site footer, in two variants.
 *
 * Links point only at routes that already exist. The reference site's
 * Customer Care / About / Follow Us columns are deliberately omitted:
 * this app has no social accounts and no policy pages, so those links
 * would be dead or fabricated.
 */
const COLUMNS: { headingKey: string; links: { path: string; labelKey: string }[] }[] = [
  {
    headingKey: 'footer_account',
    links: [
      { path: '/account', labelKey: 'account' },
      { path: '/withdrawal', labelKey: 'withdraw' },
      { path: '/withdrawal-history', labelKey: 'withdrawal_history' },
      { path: '/password', labelKey: 'password' },
    ],
  },
  {
    headingKey: 'footer_orders',
    links: [
      { path: '/order', labelKey: 'start' },
      { path: '/detail', labelKey: 'detail' },
    ],
  },
  {
    headingKey: 'footer_help',
    links: [{ path: '/support', labelKey: 'support' }],
  },
];

const COPYRIGHT = 'Copyright © 2025 Bonia. All Rights Reserved.';

interface SiteFooterProps {
  /**
   * `desktop` (the default) is the `<footer>` landmark, hidden below lg.
   * `mobile` is the same content laid out for a narrow screen.
   */
  variant?: 'desktop' | 'mobile';
}

const SiteFooter: React.FC<SiteFooterProps> = ({ variant = 'desktop' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  /** One column of headed links, shared by both variants. */
  const column = (
    { headingKey, links }: (typeof COLUMNS)[number],
    headingClass: string,
    linkClass: string
  ) => (
    <div key={headingKey}>
      <h2 className={headingClass}>{t(headingKey)}</h2>
      <ul className="list-none p-0 m-0 flex flex-col gap-2">
        {links.map(({ path, labelKey }) => (
          <li key={path}>
            <button
              type="button"
              onClick={() => navigate(path)}
              className={linkClass}
            >
              {t(labelKey)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  if (variant === 'mobile') {
    /*
      Rendered as a <div>, not a <footer>. It is mounted inside AppShell's
      <main> — the only region that scrolls below lg, so a footer has to live
      there to scroll up from under the content instead of sitting as a fixed
      band above the bottom nav. A <footer> there would either be a second
      contentinfo landmark competing with the desktop one, or a non-landmark
      depending on how the AT scopes it; a <div> makes that unambiguous.
    */
    return (
      <div className="lg:hidden bg-brand-900 text-brand-300 px-6 pt-10 pb-8 mt-12">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8">
          {COLUMNS.map((col) =>
            column(
              col,
              'font-display text-base text-brand-50 m-0 mb-3',
              'bg-transparent border-0 p-0 cursor-pointer text-left text-[13px] text-brand-300 transition-colors duration-150 active:text-accent-500'
            )
          )}
        </div>
        <div className="mt-10 pt-5 border-0 border-t border-solid border-brand-800 text-[11px] leading-relaxed text-brand-600 text-center">
          {COPYRIGHT}
        </div>
      </div>
    );
  }

  return (
    // The page owns its bottom spacing; the footer imposes none of its own.
    // `lg:mt-auto` only soaks up leftover space in a short page's flex column
    // (AppShell's `lg:min-h-[100dvh]`) so the footer doesn't float mid-page —
    // it collapses to 0 when there is no slack, so it never reintroduces a gap.
    // `relative` is load-bearing: AppShell's backdrop is an absolutely
    // positioned `z-0` element, which paints above the background of any
    // non-positioned sibling. Without it the backdrop bled through on /account.
    <footer className="relative hidden lg:block lg:mt-auto bg-brand-900 text-brand-300">
      <Container className="py-14">
        <div className="grid grid-cols-3 gap-10">
          {COLUMNS.map((col) =>
            column(
              col,
              'font-display text-lg text-brand-50 m-0 mb-4',
              'bg-transparent border-0 p-0 cursor-pointer text-sm text-brand-300 hover:text-accent-500 transition-colors duration-150'
            )
          )}
        </div>
        <div className="mt-12 pt-6 border-0 border-t border-solid border-brand-800 text-xs text-brand-600">
          {COPYRIGHT}
        </div>
      </Container>
    </footer>
  );
};

export default SiteFooter;
